import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { UserButton, useClerk } from "@clerk/react";
import { LayoutGrid, Code2, Megaphone, Crown, LifeBuoy, Activity, Bot, ClipboardList, FlaskConical, Rocket, Stamp, ScrollText, Plug, TerminalSquare, Menu, X, ShieldAlert } from "lucide-react";
import { API, useApi } from "@/lib/api";
import { Loading } from "@/components/kit";

export const NAV = [
  { section: "Operations Centre" },
  { to: "/ops", label: "All Departments", icon: LayoutGrid, id: "ops" },
  { to: "/ops/owner", label: "My Tasks & Approvals", icon: Crown, id: "ops-owner" },
  { to: "/ops/development", label: "Development", icon: Code2, id: "ops-development" },
  { to: "/ops/marketing", label: "Marketing", icon: Megaphone, id: "ops-marketing" },
  { to: "/ops/support", label: "Support", icon: LifeBuoy, id: "ops-support" },
  { section: "Gateway" },
  { to: "/", label: "Operations Overview", icon: Activity, id: "overview" },
  { to: "/agents", label: "Agent Registry", icon: Bot, id: "agents" },
  { to: "/tasks", label: "Development Tasks", icon: ClipboardList, id: "tasks" },
  { to: "/qa", label: "Independent QA", icon: FlaskConical, id: "qa" },
  { to: "/releases", label: "Release Management", icon: Rocket, id: "releases" },
  { to: "/approvals", label: "Approval Queue", icon: Stamp, id: "approvals" },
  { to: "/audit", label: "Audit History", icon: ScrollText, id: "audit" },
  { to: "/connectors", label: "Connector Status", icon: Plug, id: "connectors" },
  { to: "/console", label: "Developer Console", icon: TerminalSquare, id: "console" },
];

const Brand = () => (
  <Link to="/ops" data-testid="brand-logo-link" aria-label="Cruise Command — All Departments" className="block px-2 pt-1">
    <img src="/brand/logo-white.png" alt="Cruise Command" width="200" height="62" className="block h-auto w-[200px]" data-testid="brand-logo" />
    <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#8FA6C4]">Developer Gateway</p>
  </Link>
);

const BrandCompact = () => (
  <Link to="/ops" data-testid="brand-logo-link-mobile" aria-label="Cruise Command — All Departments" className="flex items-center gap-3">
    <img src="/brand/icon-white.png" alt="Cruise Command" width="31" height="36" className="block h-9 w-auto" data-testid="brand-logo-mobile" />
    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8FA6C4]">Developer Gateway</span>
  </Link>
);

const NavItems = ({ onPick }) => (
  <nav className="mt-6 flex flex-col gap-0.5">
    {NAV.map(({ to, label, icon: Icon, id, section }) => section ? (
      <p key={section} className="mb-1 mt-5 px-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#5E7697] first:mt-0">{section}</p>
    ) : (
      <NavLink key={to} to={to} end={to === "/" || to === "/ops"} onClick={onPick} data-testid={`nav-${id}`}
        className={({ isActive }) => `group flex items-center gap-3 border-l-2 px-3 py-2.5 text-[13px] transition-colors ${isActive ? "border-[var(--cc-signal)] bg-white/10 text-white" : "border-transparent text-[#A9BBD3] hover:bg-white/5 hover:text-white"}`}>
        <Icon size={16} strokeWidth={1.75} />
        {label}
      </NavLink>
    ))}
  </nav>
);

const PhaseBadge = () => {
  const [rel, setRel] = useState(null);
  useEffect(() => { fetch(`${API}/release`).then((r) => r.json()).then(setRel).catch(() => {}); }, []);
  return (
  <div className="rounded-sm border border-white/10 bg-white/5 p-3">
    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8FA6C4]">Authorization</p>
    <p className="mt-1 text-xs text-white">PO-CC-AOS-001 · Phase 1</p>
    <p className="mt-1 text-[11px] text-[var(--cc-signal)]">Ceiling: L1 Observer · read-only</p>
    {rel && <p className="mt-2 font-mono text-[10px] text-[#8FA6C4]" data-testid="release-identity">v{rel.version} · {rel.code_fingerprint}</p>}
  </div>
  );
};

const AccessDenied = ({ message }) => {
  const { signOut } = useClerk();
  return (
    <div className="grid min-h-screen place-items-center bg-[var(--cc-navy)] p-6 text-white" data-testid="access-denied">
      <div className="max-w-md text-center">
        <ShieldAlert className="mx-auto text-[var(--cc-deny)]" size={40} />
        <h1 className="mt-4 text-3xl font-extrabold">Access denied</h1>
        <p className="mt-3 text-sm text-[#A9BBD3]">{message}</p>
        <button data-testid="access-denied-signout" onClick={() => signOut()} className="mt-6 rounded-sm bg-white px-5 py-2 text-sm font-medium text-[var(--cc-navy)] hover:bg-[var(--cc-signal)]">Sign out</button>
      </div>
    </div>
  );
};

export default function Shell() {
  const call = useApi();
  const [me, setMe] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => { call("/me").then(setMe).catch(setError); }, [call]);
  useEffect(() => setOpen(false), [loc.pathname]);

  if (error) return <AccessDenied message={error.status === 403 ? error.message : `Unable to verify access: ${error.message}`} />;
  if (!me) return <div className="p-10"><Loading /></div>;
  return (
    <div className="min-h-screen lg:flex">
      <aside className="cc-grid-bg sticky top-0 hidden h-screen w-64 shrink-0 flex-col justify-between overflow-y-auto bg-[var(--cc-navy)] px-4 py-6 lg:flex">
        <div><Brand /><NavItems /></div>
        <PhaseBadge />
      </aside>
      <header className="sticky top-0 z-30 flex items-center justify-between bg-[var(--cc-navy)] px-4 py-3 lg:hidden">
        <BrandCompact />
        <button data-testid="mobile-nav-toggle" onClick={() => setOpen(!open)} className="text-white">{open ? <X /> : <Menu />}</button>
      </header>
      {open && <div className="fixed inset-x-0 bottom-0 top-[60px] z-20 overflow-y-auto bg-[var(--cc-navy)] px-4 pb-6 lg:hidden" data-testid="mobile-nav"><NavItems onPick={() => setOpen(false)} /><div className="mt-4"><PhaseBadge /></div></div>}
      <main className="min-w-0 flex-1">
        <div className="flex items-center justify-end gap-3 border-b border-[var(--cc-line)] bg-white/60 px-5 py-2.5 backdrop-blur md:px-10">
          <span className="hidden font-mono text-[11px] text-[var(--cc-muted)] sm:inline" data-testid="signed-in-email">{me.actor} · Product Owner</span>
          <UserButton />
        </div>
        <div className="mx-auto max-w-[1760px] px-5 py-8 md:px-10 md:py-10"><Outlet context={{ me }} /></div>
      </main>
    </div>
  );
}
